import { dangerousAxiosInstanceUseOnlyIfNeeded } from '@lumapps/base-api';

import { FILE_FIELDS } from '../constants';
import { GDFile, GDPickerConfiguration } from '../types';
import { getDriveToken } from '../utils/getDriveToken';

const GOOGLE_API_URL = 'https://www.googleapis.com';
const GOOGLE_FILES_BASE_URL = `${GOOGLE_API_URL}/drive/v3/files`;

export interface BatchGoogleDriveFilesRequestParam {
    isGoogleUser?: boolean;
    token?: GDPickerConfiguration['token'];
    filesId?: string[];
    batchParams?: { fields?: string; supportsAllDrives?: boolean };
}

/**
 * Generates the payload for a batch request to retrieve multiple Google Drive files.
 * @param filesId - An array of file IDs to retrieve.
 * @param token - The authentication token for Google Drive API.
 * @param batchParams - Optional parameters for the batch request.
 */
const getBatchRequestPayload = (
    filesId: BatchGoogleDriveFilesRequestParam['filesId'],
    token: string,
    batchParams: BatchGoogleDriveFilesRequestParam['batchParams'],
) => {
    if (!filesId) {
        return undefined;
    }

    let stringRequest = '--END_OF_PART\n';

    filesId.forEach((fileId, index) => {
        stringRequest = stringRequest.concat(
            `Content-Type: application/http\n content-id: ${
                index + 1
            }\nContent-Transfer-Encoding: binary\n\nGET ${GOOGLE_FILES_BASE_URL}/${fileId}?fields=${
                batchParams?.fields || FILE_FIELDS
            }&supportsAllDrives=${
                batchParams?.supportsAllDrives || true
            }\nAuthorization: Bearer ${token}\nContent-type: application/json\n--END_OF_PART${
                filesId.length - 1 === index ? '--' : ''
            }\n`,
        );
    });

    return stringRequest;
};

/**
 * Retrieves multiple Google Drive files in a single batch request.
 * @param params - Parameters for the batch request.
 * @param signal - An optional AbortSignal to cancel the request.
 */
export const getBatchGoogleDriveFilesApi = async (params?: BatchGoogleDriveFilesRequestParam, signal?: AbortSignal) => {
    if (!params || !params.filesId?.length || !params.isGoogleUser) {
        return [];
    }

    const { filesId, token: tokenConfig, batchParams } = params;

    const token = await getDriveToken({ fetchKey: 'getToken', token: tokenConfig });

    if (!token) {
        return [];
    }

    const requestPayload = getBatchRequestPayload(filesId, token, batchParams);

    const { data } = await dangerousAxiosInstanceUseOnlyIfNeeded.post(
        `${GOOGLE_API_URL}/batch/drive/v3`,
        requestPayload,
        {
            signal,
            headers: {
                'Content-Type': 'multipart/mixed; boundary=END_OF_PART',
            },
        },
    );

    // get the files from string thanks to the --batch delimeter
    const filesJSON = data.split('--batch').slice(1, -1);
    // todo: handle errors (eg.: invalid file id)
    const parsedFiles: GDFile[] = filesJSON.map((fileJSON: string) => {
        const start = fileJSON.indexOf('{');
        const end = fileJSON.lastIndexOf('}') + 1;
        const fileString = fileJSON.slice(start, end);

        return JSON.parse(fileString);
    });

    return parsedFiles;
};

export interface GoogleDriveFolderContentRequestParam {
    folderId: string;
    isGoogleUser?: boolean;
    listParams?: {
        fields?: string;
        pageSize?: number;
        supportsAllDrives?: boolean;
    };
    nextPageToken?: string;
    searchTerm?: string;
    token?: GDPickerConfiguration['token'];
}

/**
 * Retrieves the content of a Google Drive folder along with the folder's metadata.
 * @param params - Parameters for the folder content request.
 * @param signal - An optional AbortSignal to cancel the request.
 */
export const getGoogleDriveFolderContentApi = async (
    params: GoogleDriveFolderContentRequestParam,
    signal?: AbortSignal,
): Promise<{ folder: GDFile; files: GDFile[]; nextPageToken?: string }> => {
    if (!params || !params.folderId || !params.isGoogleUser) {
        return { folder: {} as GDFile, files: [] };
    }

    const { folderId, token: tokenConfig, listParams, nextPageToken, searchTerm } = params;

    const token = await getDriveToken({ fetchKey: 'getToken', token: tokenConfig });

    if (!token) {
        return { folder: {} as GDFile, files: [] };
    }

    // Get folder metadata
    const folderResponse = dangerousAxiosInstanceUseOnlyIfNeeded.get(
        `${GOOGLE_FILES_BASE_URL}/${folderId}?fields=${FILE_FIELDS}&supportsAllDrives=true`,
        {
            signal,
            headers: {
                Authorization: `Bearer ${token}`,
            },
        },
    );

    // Get folder content
    let query = `'${folderId}' in parents`;

    if (searchTerm) {
        query += ` and name contains '${searchTerm}'`; // Add search term to the query
    }
    const fields = listParams?.fields || `files(${FILE_FIELDS}),nextPageToken,kind`;
    const pageSize = listParams?.pageSize || 10;
    const supportsAllDrives = listParams?.supportsAllDrives || true;

    const folderContentResponse = await dangerousAxiosInstanceUseOnlyIfNeeded.get(
        `${GOOGLE_FILES_BASE_URL}?q=${encodeURIComponent(query)}&fields=${encodeURIComponent(
            fields,
        )}&pageSize=${pageSize}&supportsAllDrives=${supportsAllDrives}${
            nextPageToken ? `&pageToken=${nextPageToken}` : ''
        }`,
        {
            signal,
            headers: {
                Authorization: `Bearer ${token}`,
            },
        },
    );

    return {
        folder: (await folderResponse).data,
        files: folderContentResponse.data.files || [],
        nextPageToken: folderContentResponse.data.nextPageToken,
    };
};

export const googleDriveQueryKeys = {
    all: () => ['google-drive'] as const,
    'batch-files': (params?: BatchGoogleDriveFilesRequestParam) =>
        [...googleDriveQueryKeys.all(), 'batch-files', params] as const,
    'folder-content': (params?: GoogleDriveFolderContentRequestParam) =>
        [...googleDriveQueryKeys.all(), 'folder-content', params] as const,
};
